// ═══════════════════════════════════════════════════════════════
// COMPONENTE REUTILIZABLE: Sección de Informes Mensuales
// Funciona para 'temperatura', 'limpieza' y 'plagas'
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { 
  FileText, XCircle, CheckCircle2, AlertCircle, 
  Send, Trash2, Eye, RotateCcw 
} from 'lucide-react';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

// Configuración por tipo: cómo mostrar el resumen estadístico
const TIPO_CONFIG = {
  temperatura: {
    nombre: 'Temperatura',
    iconoTabla: ['Fecha', 'Hora', 'Zona', 'T. Actual', 'Humedad', 'Operario', 'Estado'],
    renderFila: (reg) => (
      <>
        <td>{new Date(reg.fecha).toLocaleDateString('es-ES')}</td>
        <td style={{ fontFamily: 'var(--font-mono)' }}>{reg.hora}</td>
        <td>{reg.zona}</td>
        <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{reg.temperatura_actual != null ? `${reg.temperatura_actual}°C` : '—'}</td>
        <td style={{ fontFamily: 'var(--font-mono)' }}>{reg.humedad != null ? `${reg.humedad}%` : '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{reg.operario_nombre}</td>
        <td style={{ fontSize: '0.8rem' }}>
          {reg.estado === 'fuera_rango' ? <span style={{ color: 'var(--color-danger)' }}>⚠ Fuera</span> : 
           reg.estado === 'incidencia' ? <span style={{ color: 'var(--color-warning)' }}>⚡ Incid.</span> :
           <span style={{ color: 'var(--color-success)' }}>✓ OK</span>}
        </td>
      </>
    ),
    renderResumen: (r) => (
      <>
        <StatBox label="Total registros" valor={r.total_registros || 0} color="#3b82f6" />
        <StatBox label="T. Media" valor={r.temperatura?.media ? `${r.temperatura.media}°C` : '—'} color="#10b981" />
        <StatBox label="T. Máxima" valor={r.temperatura?.maxima ? `${r.temperatura.maxima}°C` : '—'} color="#f59e0b" />
        <StatBox label="T. Mínima" valor={r.temperatura?.minima ? `${r.temperatura.minima}°C` : '—'} color="#6366f1" />
        <StatBox label="Humedad media" valor={r.humedad?.media ? `${r.humedad.media}%` : '—'} color="#06b6d4" />
        <StatBox label="Fuera de rango" valor={r.registros_fuera_rango || 0} color="#dc2626" />
        <StatBox label="Cumplimiento" valor={`${r.porcentaje_cumplimiento || 100}%`} color="#10b981" />
      </>
    ),
    extraInfo: (r) => r.zonas_monitorizadas?.length > 0 && (
      <div style={{ marginBottom: 24, padding: 12, background: 'var(--color-gray-1)', borderRadius: 8 }}>
        <strong style={{ fontSize: '0.85rem' }}>Zonas monitorizadas:</strong>{' '}
        {r.zonas_monitorizadas.join(' · ')}
      </div>
    )
  },
  limpieza: {
    nombre: 'Limpieza',
    iconoTabla: ['Fecha', 'Zona', 'Tipo', 'Producto', 'Operario', 'Estado'],
    renderFila: (reg) => (
      <>
        <td>{new Date(reg.fecha).toLocaleDateString('es-ES')}</td>
        <td>{reg.zona}</td>
        <td style={{ fontSize: '0.82rem', textTransform: 'capitalize' }}>{reg.tipo_limpieza || '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{reg.producto_usado || '—'}</td>
        <td style={{ fontSize: '0.82rem' }}>{reg.operario_nombre}</td>
        <td style={{ fontSize: '0.8rem' }}>
          {reg.estado === 'completado' ? <span style={{ color: 'var(--color-success)' }}>✓ Completado</span> : 
           reg.estado === 'pendiente' ? <span style={{ color: 'var(--color-warning)' }}>⏳ Pdte.</span> :
           <span style={{ color: 'var(--color-danger)' }}>⚠ Incidencia</span>}
        </td>
      </>
    ),
    renderResumen: (r) => (
      <>
        <StatBox label="Total registros" valor={r.total_registros || 0} color="#3b82f6" />
        <StatBox label="Completados" valor={r.completados || 0} color="#10b981" />
        <StatBox label="Pendientes" valor={r.pendientes || 0} color="#f59e0b" />
        <StatBox label="Zonas" valor={r.zonas?.length || 0} color="#6366f1" />
      </>
    ),
    extraInfo: (r) => (
      <>
        {r.zonas?.length > 0 && (
          <div style={{ marginBottom: 12, padding: 12, background: 'var(--color-gray-1)', borderRadius: 8 }}>
            <strong style={{ fontSize: '0.85rem' }}>Zonas limpiadas:</strong>{' '}
            {r.zonas.join(' · ')}
          </div>
        )}
        {r.tipos_limpieza?.length > 0 && (
          <div style={{ marginBottom: 24, padding: 12, background: 'var(--color-gray-1)', borderRadius: 8 }}>
            <strong style={{ fontSize: '0.85rem' }}>Tipos de limpieza:</strong>{' '}
            {r.tipos_limpieza.join(' · ')}
          </div>
        )}
      </>
    )
  },
  plagas: {
    nombre: 'Control de Plagas',
    iconoTabla: ['Fecha', 'Empresa', 'Tipo', 'Resultado', 'Próxima'],
    renderFila: (reg) => (
      <>
        <td>{new Date(reg.fecha_revision).toLocaleDateString('es-ES')}</td>
        <td style={{ fontWeight: 600 }}>{reg.empresa}</td>
        <td style={{ fontSize: '0.82rem' }}>{reg.tipo_revision}</td>
        <td style={{ fontSize: '0.8rem' }}>
          {reg.resultado === 'satisfactorio' ? <span style={{ color: 'var(--color-success)' }}>✓ OK</span> :
           reg.resultado === 'con_incidencias' ? <span style={{ color: 'var(--color-warning)' }}>⚠ Incid.</span> :
           <span style={{ color: 'var(--color-danger)' }}>❌ No sat.</span>}
        </td>
        <td style={{ fontSize: '0.82rem' }}>{reg.proxima_revision ? new Date(reg.proxima_revision).toLocaleDateString('es-ES') : '—'}</td>
      </>
    ),
    renderResumen: (r) => (
      <>
        <StatBox label="Total revisiones" valor={r.total_revisiones || 0} color="#3b82f6" />
        <StatBox label="Satisfactorias" valor={r.satisfactorias || 0} color="#10b981" />
        <StatBox label="Con incidencias" valor={r.con_incidencias || 0} color="#f59e0b" />
        <StatBox label="Empresas" valor={r.empresas?.length || 0} color="#6366f1" />
      </>
    ),
    extraInfo: (r) => r.empresas?.length > 0 && (
      <div style={{ marginBottom: 24, padding: 12, background: 'var(--color-gray-1)', borderRadius: 8 }}>
        <strong style={{ fontSize: '0.85rem' }}>Empresas:</strong>{' '}
        {r.empresas.join(' · ')}
      </div>
    )
  }
};

// ═══════════════════════════════════════════════════════════════
// SUB-COMPONENTES
// ═══════════════════════════════════════════════════════════════
function StatBox({ label, valor, color }) {
  return (
    <div style={{ padding: 14, background: 'var(--color-gray-1)', borderRadius: 8, border: `2px solid ${color}22`, borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: '0.75rem', color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: '1.4rem', fontWeight: 700, color: color, fontFamily: 'var(--font-mono)' }}>{valor}</div>
    </div>
  );
}

function EstadoBadge({ estado }) {
  const config = {
    borrador: { bg: '#dbeafe', color: '#1e40af', label: '📝 Borrador' },
    pendiente_firma: { bg: '#fef3c7', color: '#92400e', label: '⏳ Pdte. firma' },
    firmado: { bg: '#dcfce7', color: '#166534', label: '✅ Firmado' },
    rechazado: { bg: '#fecaca', color: '#991b1b', label: '❌ Rechazado' }
  };
  const c = config[estado] || config.borrador;
  return (
    <span style={{ padding: '3px 10px', borderRadius: 12, background: c.bg, color: c.color, fontSize: '0.75rem', fontWeight: 600 }}>
      {c.label}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Generar nuevo informe mensual
// ═══════════════════════════════════════════════════════════════
function ModalGenerarInforme({ tipo, onClose, onGenerado }) {
  const hoy = new Date();
  const mesAnterior = hoy.getMonth() === 0 ? 12 : hoy.getMonth();
  const anioMesAnterior = hoy.getMonth() === 0 ? hoy.getFullYear() - 1 : hoy.getFullYear();
  
  const [anio, setAnio] = useState(anioMesAnterior);
  const [mes, setMes] = useState(mesAnterior);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGenerar = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post('/informes/mensuales/generar', { tipo, anio: parseInt(anio), mes: parseInt(mes) });
      onGenerado(res.data.informe);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al generar el informe');
    } finally { setLoading(false); }
  };

  const anios = [];
  for (let a = hoy.getFullYear(); a >= hoy.getFullYear() - 4; a--) anios.push(a);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={20} color="var(--color-primary)" /> Generar Informe Mensual
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-danger"><AlertCircle size={16} /> {error}</div>}
          <p style={{ color: 'var(--color-text-2)', marginBottom: 16, fontSize: '0.9rem' }}>
            Se generará un informe con todos los registros del mes seleccionado, incluyendo estadísticas y lista completa.
          </p>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Mes <span className="required">*</span></label>
              <select className="form-control" value={mes} onChange={e => setMes(e.target.value)}>
                {MESES.map((m, i) => <option key={i+1} value={i+1}>{m}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Año <span className="required">*</span></label>
              <select className="form-control" value={anio} onChange={e => setAnio(e.target.value)}>
                {anios.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleGenerar} disabled={loading}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <CheckCircle2 size={16} />}
            Generar Informe
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Ver detalle del informe
// ═══════════════════════════════════════════════════════════════
function ModalVerInforme({ tipo, informeId, onClose }) {
  const [informe, setInforme] = useState(null);
  const [loading, setLoading] = useState(true);
  const config = TIPO_CONFIG[tipo];

  useEffect(() => {
    api.get(`/informes/mensuales/${informeId}`).then(r => setInforme(r.data)).finally(() => setLoading(false));
  }, [informeId]);

  if (loading) return <div className="modal-overlay"><div className="modal"><div className="modal-body"><div className="spinner" /></div></div></div>;
  if (!informe) return null;

  const r = informe.datos_resumen || {};
  const registros = informe.datos_json || [];

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: 0 }}>
              <FileText size={20} color="var(--color-primary)" /> {informe.titulo}
            </h2>
            <div style={{ fontSize: '0.85rem', color: 'var(--color-text-3)', marginTop: 4 }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 700 }}>{informe.codigo}</span>
              {' · '}
              <EstadoBadge estado={informe.estado} />
            </div>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <h3 style={{ marginTop: 0, fontSize: '1.05rem' }}>📊 Resumen Ejecutivo</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
            {config.renderResumen(r)}
          </div>

          {config.extraInfo(r)}

          <h3 style={{ fontSize: '1.05rem' }}>📋 Registros del mes ({registros.length})</h3>
          <div className="table-wrap" style={{ maxHeight: 400, overflowY: 'auto' }}>
            <table>
              <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
                <tr>{config.iconoTabla.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {registros.map(reg => (
                  <tr key={reg.id}>{config.renderFila(reg)}</tr>
                ))}
              </tbody>
            </table>
          </div>

          {informe.fecha_firma && (
            <div style={{ marginTop: 20, padding: 16, background: '#f0fdf4', borderRadius: 8, border: '1px solid #86efac' }}>
              <div style={{ fontWeight: 700, color: '#166534' }}>✅ Firmado por {informe.firmado_nombre}</div>
              <div style={{ fontSize: '0.85rem', color: '#15803d', marginTop: 4 }}>
                {new Date(informe.fecha_firma).toLocaleString('es-ES')}
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Eliminar informe (doble confirmación)
// ═══════════════════════════════════════════════════════════════
function ModalEliminarInforme({ informe, onClose, onEliminado }) {
  const [codigo, setCodigo] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEliminar = async () => {
    setLoading(true); setError('');
    try {
      await api.delete(`/informes/mensuales/${informe.id}`, { data: { confirmacion_codigo: codigo, password } });
      onEliminado();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al eliminar');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-danger)' }}>
            <Trash2 size={20} /> Eliminar Informe
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-danger"><AlertCircle size={16} /> Esta acción no se puede deshacer fácilmente.</div>
          <p>Para confirmar, escribe el código del informe y tu contraseña.</p>
          <div className="form-group">
            <label className="form-label">Código del informe: <strong style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-mono)' }}>{informe.codigo}</strong></label>
            <input type="text" className="form-control" value={codigo} onChange={e => setCodigo(e.target.value)} placeholder={informe.codigo} />
          </div>
          <div className="form-group">
            <label className="form-label">Tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} />
          </div>
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-danger" onClick={handleEliminar} disabled={loading || !codigo || !password}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Trash2 size={16} />}
            Eliminar definitivamente
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MODAL: Revertir informe a borrador
// ═══════════════════════════════════════════════════════════════
function ModalRevertirInforme({ informe, onClose, onRevertido }) {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleRevertir = async () => {
    if (!password) { setError('Debes introducir tu contraseña'); return; }
    setLoading(true); setError('');
    try {
      await api.put(`/informes/mensuales/${informe.id}/reset-estado`, { nuevo_estado: 'borrador', password });
      onRevertido();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al revertir');
    } finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--color-warning)' }}>
            <RotateCcw size={20} /> Revertir a Borrador
          </h2>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><XCircle size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-warning"><AlertCircle size={16} /> 
            El informe volverá a estado "Borrador". Si ya se había enviado solicitud de firma, esta se cancelará automáticamente.
          </div>
          <div style={{ padding: 12, background: 'var(--color-gray-1)', borderRadius: 8, marginBottom: 16 }}>
            <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 700 }}>{informe.codigo}</div>
            <div style={{ fontSize: '0.9rem', marginTop: 4 }}>{informe.titulo}</div>
          </div>
          <div className="form-group">
            <label className="form-label">🔐 Confirma con tu contraseña</label>
            <input type="password" className="form-control" value={password} onChange={e => setPassword(e.target.value)} placeholder="Tu contraseña actual" />
          </div>
          {error && <div className="alert alert-danger">{error}</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={handleRevertir} disabled={loading || !password}
            style={{ background: 'var(--color-warning)' }}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <RotateCcw size={16} />}
            Revertir a borrador
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL: Sección de Informes Mensuales
// ═══════════════════════════════════════════════════════════════
export default function InformesMensualesSection({ tipo, headerTitle }) {
  const [informes, setInformes] = useState([]);
  const [modalInforme, setModalInforme] = useState(false);
  const [verInformeId, setVerInformeId] = useState(null);
  const [eliminarInforme, setEliminarInforme] = useState(null);
  const [revertirInforme, setRevertirInforme] = useState(null);
  const [success, setSuccess] = useState('');

  const isAdmin = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return false;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.username === 'admin' || payload.rol === 'administrador';
    } catch { return false; }
  })();

  const loadInformes = () => {
    api.get('/informes/mensuales', { params: { tipo } }).then(r => setInformes(r.data)).catch(() => setInformes([]));
  };

  useEffect(() => { loadInformes(); }, [tipo]);

  const handleInformeGenerado = (informe) => {
    setModalInforme(false);
    setSuccess(`Informe ${informe.codigo} generado correctamente.`);
    loadInformes();
    setTimeout(() => setSuccess(''), 5000);
  };

  const handlePasarPdteFirma = async (informe) => {
    if (!window.confirm(`¿Marcar el informe ${informe.codigo} como "Pendiente de firma"?\n\nDespués podrás enviar la solicitud a la Directora Técnica desde el módulo de Firmas.`)) return;
    try {
      await api.put(`/informes/mensuales/${informe.id}/pasar-pdte-firma`);
      setSuccess(`Informe ${informe.codigo} marcado como pendiente de firma. Ahora ve a Firmas Pendientes.`);
      loadInformes();
      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      alert(err.response?.data?.error || 'Error');
    }
  };

  return (
    <>
      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}><CheckCircle2 size={16} /> {success}</div>}

      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn btn-outline" onClick={() => setModalInforme(true)}>
          <FileText size={16} /> Generar Informe Mensual
        </button>
      </div>

      {informes.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FileText size={18} color="var(--color-primary)" />
              {headerTitle || 'Informes Mensuales'}
            </h3>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-3)' }}>{informes.length} informe(s)</span>
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Período</th>
                  <th>Registros</th>
                  <th>Incidencias</th>
                  <th>Estado</th>
                  <th>Creado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {informes.map(inf => (
                  <tr key={inf.id}>
                    <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-primary)', fontWeight: 600 }}>{inf.codigo}</td>
                    <td>{MESES[inf.mes - 1]} {inf.anio}</td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)' }}>{inf.total_registros}</td>
                    <td style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', color: inf.incidencias > 0 ? 'var(--color-danger)' : 'var(--color-success)', fontWeight: 600 }}>
                      {inf.incidencias}
                    </td>
                    <td><EstadoBadge estado={inf.estado} /></td>
                    <td style={{ fontSize: '0.82rem', color: 'var(--color-text-3)' }}>
                      {new Date(inf.created_at).toLocaleDateString('es-ES')}
                      <div style={{ fontSize: '0.75rem' }}>{inf.creado_nombre}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setVerInformeId(inf.id)} title="Ver detalle">
                          <Eye size={14} />
                        </button>
                        {inf.estado === 'borrador' && (
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => handlePasarPdteFirma(inf)} title="Pasar a Pdte. firma"
                            style={{ color: 'var(--color-warning)' }}>
                            <Send size={14} />
                          </button>
                        )}
                        {inf.estado === 'pendiente_firma' && isAdmin && (
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setRevertirInforme(inf)} title="Revertir a borrador (admin)"
                            style={{ color: 'var(--color-warning)' }}>
                            <RotateCcw size={14} />
                          </button>
                        )}
                        {inf.estado === 'borrador' && (
                          <button className="btn btn-ghost btn-icon btn-sm" onClick={() => setEliminarInforme(inf)} title="Eliminar"
                            style={{ color: 'var(--color-danger)' }}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modalInforme && <ModalGenerarInforme tipo={tipo} onClose={() => setModalInforme(false)} onGenerado={handleInformeGenerado} />}
      {verInformeId && <ModalVerInforme tipo={tipo} informeId={verInformeId} onClose={() => setVerInformeId(null)} />}
      {eliminarInforme && <ModalEliminarInforme informe={eliminarInforme} onClose={() => setEliminarInforme(null)} onEliminado={() => { setEliminarInforme(null); loadInformes(); setSuccess('Informe eliminado'); setTimeout(() => setSuccess(''), 3000); }} />}
      {revertirInforme && <ModalRevertirInforme informe={revertirInforme} onClose={() => setRevertirInforme(null)} onRevertido={() => { setRevertirInforme(null); loadInformes(); setSuccess('Informe revertido a Borrador'); setTimeout(() => setSuccess(''), 3000); }} />}
    </>
  );
}
